/**
 * @author Fernando Ruano
 * @file saga.js
 * @description Patient Saga
 */

// Dependencies
import { put, call, takeLatest } from 'redux-saga/effects';

// Global actions
import { setResponse, setLoading } from '@nephro/redux/stores/app/actions';

// Services
import patientService from '@nephro/services/patient-service';

// Constants
import {
  CREATE_PATIENT,
  DELETE_PATIENT_BY_ID,
  GET_ALL_PATIENTS,
  GET_PATIENT_BY_ID,
  UPDATE_PATIENT,
  UPDATE_PATIENT_STATUS,
  GET_ALL_IDENTIFICATION_TYPES,
  GET_ALL_PATIENT_STATUS,
  GET_PATIENT_BY_DOCUMENT,
  CREATE_PATIENT_OKTA,
  GET_PATIENT_INDICATORS,
  GET_EVENTS_BY_PATIENT_ID,
  GET_FILTER_PATIENTS,
  GET_PATIENT_EXTRA_DATA_BY_ID,
  GET_PTH_PATIENT,
  GET_UPCOMING_VIDEO_CALLS,
  GET_LAST_APPOINTMENT,
  GET_EMAIL_PATIENT_BY_DOCUMENT,
  SEND_MASS_INVITATION,
  GET_LIST_PATIENT_M_PROCESSING,
  GET_PATIENT_M_DETAILS
} from './constants';

const customError = {
  type: 'Error',
  message: 'commons.problem'
};

/**
 * Call service to get patients from backend
 * @param { object } action - Action with payload data
 */
export function* getPatientsArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const { callback } = action;

  try {
    const response = yield call(patientService.getPatients);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get patient info by id
 * @param { object } action - Action with payload data
 */
export function* getPatientById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(patientService.getPatientById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.patientNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient last appointment by id versia
 * @param { object } action - Action with payload data
 */
export function* getPatientLastAppointment(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(patientService.getPatientLastAppointment, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.patientNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient info by document
 * @param { object } action - Action with payload data
 */
export function* getPatientByDocument(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { document, documentType } = data;

  try {
    const response = yield call(
      patientService.getPatientByDocument,
      document,
      documentType
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient info by document
 * @param { object } action - Action with payload data
 */
export function* getEmailPatientByDocument(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { document, documentType } = data;

  try {
    const response = yield call(
      patientService.getEmailPatientByDocument,
      document,
      documentType
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        false,
        { ...err, data: { ...data } },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get patient extra info by document
 * @param { object } action - Action with payload data
 */
export function* getPatientExtraDataById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(patientService.getPatientExtraDataById, id);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        'errors.patientNotFound' || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to create patient
 * @param { object } action - Action with payload data
 */
export function* createPatient(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  try {
    const response = yield call(patientService.createPatient, data);
    yield put(
      setResponse(
        'success',
        'new patient',
        'patientMg.patientCreated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to create patient in Okta
 * @param { object } action - Action with payload data
 */
export function* createPatientOkta(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;

  const oktaData = {
    firstName: data.first_name,
    lastName: data.last_name,
    email: data.email,
    active: true,
    description: '',
    identificationNumber: '',
    password: '',
    username: data.email
  };

  try {
    const response = yield call(patientService.createPatientOkta, oktaData);

    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        `errors.${err.code}` || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to update patient
 * @param { object } action - Action with payload data
 */
export function* updatePatient(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(patientService.updatePatient, data, id);
    yield put(
      setResponse(
        'success',
        'updated patient',
        'patientMg.patientUpdated',
        response,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to update patient status
 * @param { object } action - Action with payload data
 */
export function* updatePatientStatus(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id, statusName } = data;

  try {
    const response = yield call(
      patientService.updatePatientStatus,
      id,
      statusName
    );
    yield put(setResponse('success', false, false, response, callback));
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to delete patient by id
 * @param { object } action - Action with payload data
 */
export function* deletePatientById(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { id } = data;

  try {
    const response = yield call(patientService.deletePatientById, id);
    yield put(
      setResponse(
        'success',
        'deleted patient',
        'patientMg.patientDeleted',
        { ...response },
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        'Error',
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  // yield put(setLoading(false));
}

/**
 * Call service to get identification types from backend
 * @param { object } action - Action with payload data
 */
export function* getIdentificationTypesArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit } = data;

  try {
    const response = yield call(
      patientService.getIdentificationTypes,
      paged,
      limit,
      page
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get patient status list from backend
 * @param { object } action - Action with payload data
 */
export function* getPatientStatusArray(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const { callback } = action;

  try {
    const response = yield call(patientService.getPatientStatusList);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get patient indicator info by id and Patient identification
 * @param { object } action - Action with payload data
 */
export function* getPatientIndicators(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { patientId } = data;

  try {
    const response = yield call(patientService.getPatientIndicators, patientId);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patient event info by id and Patient identification
 * @param { object } action - Action with payload data
 */
export function* getEventTypesByPatientId(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { patientId } = data;

  try {
    const response = yield call(
      patientService.getEventTypesByPatientId,
      patientId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get patients from backend based on filter
 * @param { object } action - Action with payload data
 */
export function* getFilterPatients(action) {
  yield put(setLoading(true));
  yield put(setResponse(false, false, false));
  const {
    callback,
    payload: { data }
  } = action;
  const { paged, page, limit, filter, clinicId } = data;
  try {
    const response = yield call(
      patientService.getFilterPatients,
      paged,
      limit,
      page,
      filter,
      clinicId
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
    yield put(setLoading(false));
  }
  yield put(setLoading(false));
}

/**
 * Call service to get PTH patient
 * @param { object } action - Action with payload data
 */
export function* getPthPatient(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;
  const { document, documentTypeId, result } = data;
  try {
    const response = yield call(
      patientService.getPthPatient,
      document,
      documentTypeId,
      result
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get upcoming video calls
 * @param { object } action - Action with payload data
 */
export function* getUpcomingVideoCalls(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;
  const { clinicId, limit, offset } = data;
  try {
    const response = yield call(
      patientService.getUpcomingVideoCalls,
      clinicId,
      limit,
      offset
    );
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get upcoming video calls
 * @param { object } action - Action with payload data
 */
export function* sendMassInvitation(action) {
  const {
    callback,
    payload: { data }
  } = action;
  try {
    const response = yield call(patientService.sendMassInvitation, data);
    yield put(setResponse('success', false, false, { ...response }, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  }
}

/**
 * Call service to get upcoming video calls
 * @param { object } action - Action with payload data
 */
export function* listPatientMProcessing(action) {
  yield put(setLoading(true));
  const { callback } = action;
  try {
    const response = yield call(patientService.listPatientMProcessing);

    yield put(
      setResponse(response.data, false, false, { ...response }, callback)
    );
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Call service to get upcoming video calls
 * @param { object } action - Action with payload data
 */
export function* patientMProcessDetail(action) {
  yield put(setLoading(true));
  const {
    callback,
    payload: { data }
  } = action;
  try {
    const response = yield call(patientService.patientMProcessDetail, data);
    yield put(setResponse('success', false, false, response, callback));
  } catch (err) {
    yield put(
      setResponse(
        'error',
        false,
        err.message || customError.message,
        false,
        callback
      )
    );
    yield put(setResponse(false, false, false));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Declare all component sagas
 */
export function* watchPatientSaga() {
  yield takeLatest(GET_ALL_PATIENTS, getPatientsArray);
  yield takeLatest(GET_PATIENT_BY_ID, getPatientById);
  yield takeLatest(GET_PATIENT_EXTRA_DATA_BY_ID, getPatientExtraDataById);
  yield takeLatest(GET_PATIENT_BY_DOCUMENT, getPatientByDocument);
  yield takeLatest(GET_EMAIL_PATIENT_BY_DOCUMENT, getEmailPatientByDocument);
  yield takeLatest(CREATE_PATIENT, createPatient);
  yield takeLatest(CREATE_PATIENT_OKTA, createPatientOkta);
  yield takeLatest(UPDATE_PATIENT, updatePatient);
  yield takeLatest(UPDATE_PATIENT_STATUS, updatePatientStatus);
  yield takeLatest(DELETE_PATIENT_BY_ID, deletePatientById);
  yield takeLatest(GET_ALL_IDENTIFICATION_TYPES, getIdentificationTypesArray);
  yield takeLatest(GET_ALL_PATIENT_STATUS, getPatientStatusArray);
  yield takeLatest(GET_PATIENT_INDICATORS, getPatientIndicators);
  yield takeLatest(GET_EVENTS_BY_PATIENT_ID, getEventTypesByPatientId);
  yield takeLatest(GET_FILTER_PATIENTS, getFilterPatients);
  yield takeLatest(GET_PTH_PATIENT, getPthPatient);
  yield takeLatest(GET_UPCOMING_VIDEO_CALLS, getUpcomingVideoCalls);
  yield takeLatest(GET_LAST_APPOINTMENT, getPatientLastAppointment);
  yield takeLatest(SEND_MASS_INVITATION, sendMassInvitation);
  yield takeLatest(GET_LIST_PATIENT_M_PROCESSING, listPatientMProcessing);
  yield takeLatest(GET_PATIENT_M_DETAILS, patientMProcessDetail);
}
